// src/pages/ServiceManagement/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Apps as AppsIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { servicesApi } from '../../services/apiClient';
import ServiceList from './ServiceList';
import ServiceDetails from './ServiceDetails';
import CreateServiceDialog from './CreateServiceDialog';

const ServiceManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesApi.getAll({ search: searchTerm });

      if (response.data.success) {
        setServices(response.data.data || []);
      }
    } catch (error) {
      console.error('[ServiceManagement] Error loading services:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to load services',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [searchTerm, enqueueSnackbar]);

  useEffect(() => {
    if (isAdmin) {
      loadServices();
    }
  }, [isAdmin, loadServices]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  const handleServiceCreated = (newService) => {
    setOpenCreateDialog(false);
    enqueueSnackbar('Service created successfully', { variant: 'success' });
    loadServices();
    setSelectedService(newService);
  };

  const handleServiceUpdated = () => {
    loadServices();
    if (selectedService) {
      loadServiceDetails(selectedService.id);
    }
  };

  const loadServiceDetails = async (serviceId) => {
    try {
      const response = await servicesApi.getById(serviceId);

      if (response.data.success) {
        setSelectedService(response.data.data);
      }
    } catch (error) {
      console.error('Error loading service details:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to load service details',
        { variant: 'error' }
      );
    }
  };

  const handleServiceDeleted = () => {
    setSelectedService(null);
    loadServices();
    enqueueSnackbar('Service deleted successfully', { variant: 'success' });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. Admin access required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Sidebar - Service List */}
      <Box
        sx={{
          width: 350,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AppsIcon color="primary" />
            <Typography variant="h6">
              Service Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDialog(true)}
              fullWidth
              size="small"
            >
              New Service
            </Button>
            <Button
              variant="outlined"
              onClick={loadServices}
              disabled={loading}
              size="small"
            >
              <RefreshIcon />
            </Button>
          </Box>
        </Box>

        {/* Service List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ServiceList
            services={services}
            selectedService={selectedService}
            onSelectService={handleServiceSelect}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        )}
      </Box>

      {/* Right Panel - Service Details */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedService ? (
          <ServiceDetails
            service={selectedService}
            onUpdate={handleServiceUpdated}
            onDelete={handleServiceDeleted}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3
            }}
          >
            <AppsIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select a service
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Choose a service from the list to view and edit its details
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create Service Dialog */}
      <CreateServiceDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSuccess={handleServiceCreated}
      />
    </Box>
  );
};

export default ServiceManagement;
